<script setup lang="ts">
    import { useViewControlsClusterViewModel } from '@/views/3d-planner/view-controls/useViewControlsClusterViewModel';
    import VisibilityToggleBar from '@/views/3d-planner/components/VisibilityToggleBar.vue';
    import SnapToViewButtons from './SnapToViewButtons.vue';
    import { PLANNER_TARGETS } from '@/domain/analytics/analyticsTargets';

    const vm = useViewControlsClusterViewModel();
    // No teardown needed: the ViewModel is a Pinia store (app-singleton).
    // Its internal preset-tracker watcher lives for the Pinia instance's lifetime,
    // which is reset in tests via setActivePinia(createPinia()).
</script>

<template>
    <div class="bar" :data-analytics-id="PLANNER_TARGETS.viewControlsBar">
        <visibility-toggle-bar
            class="bar-center"
            :data-analytics-id="PLANNER_TARGETS.viewModeIcons"
        />

        <div class="bar-right">
            <snap-to-view-buttons
                :active-preset="vm.activePreset"
                :data-analytics-id="PLANNER_TARGETS.snapToViewButtons"
                @select="vm.setPreset"
            />
            <v-btn
                class="ct-master-toggle"
                :data-analytics-id="PLANNER_TARGETS.ctButton"
                color="success"
                rounded="pill"
                height="36"
                @click="vm.toggleRowOpen"
            >
                {{ vm.rowOpen ? 'Hide CT Scans' : 'Show CT Scans' }}
            </v-btn>
        </div>
    </div>
</template>

<style scoped lang="scss">
    // Bar: 3-column grid so bar-center sits in the true horizontal centre
    // regardless of bar-right's width. The CT slider row above lives in
    // PlannerOverlay and covers this bar's rounded top-right corner when open.
    .bar {
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        align-items: center;
        padding: 9px 16px;
        background: rgb(var(--v-theme-dark-background));
        color: rgb(var(--v-theme-white));
        flex-shrink: 0;
        border-radius: 0 13px 0 0;

        // Don't let the browser start a text/element drag from inside the bar.
        // Without this, clicking on a button's label and dragging triggers a
        // native HTML5 drag-and-drop — the cursor turns into a no-drop icon
        // and the OrbitControls pointer handlers stop receiving events, which
        // looks like "orbit is broken" until the user clicks somewhere else.
        user-select: none;
        -webkit-user-drag: none;
    }

    .bar-center {
        // Placement only — internal layout (flex, spacing) is owned by
        // VisibilityToggleBar's own .app-planner-view-modes root.
        grid-column: 2;
    }
    .bar-right {
        grid-column: 3;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 16px;
    }

    // Always bold, always green — sits at the right of the bar. See
    // SnapToViewButtons.vue for the rationale on the !important overrides.
    .ct-master-toggle {
        flex: 0 0 auto;
        min-width: 0 !important;
        font-weight: 700 !important;
        letter-spacing: normal !important;
        padding: 0 16px !important;
        text-transform: none !important;
    }

    .bar :deep(.app-planner-view-modes) {
        margin-top: 0;
        align-items: center;

        button {
            display: flex;
            align-items: center;
        }
    }
</style>
